import React, { useEffect, useRef } from 'react';
import { Point } from '../../types/point';

interface PointMinimapProps {
  center: Point;
  areaPoints: Point[];
  size?: number;
}

/**
 * Renders a pixel-art minimap canvas centered on `center`.
 * Each cell represents one world unit; surrounding points are drawn at their
 * relative offsets from the center.
 */
export const PointMinimap: React.FC<PointMinimapProps> = ({
  center,
  areaPoints,
  size = 90,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const RADIUS = 4;                          // cells visible in each direction
  const CELL = size / (RADIUS * 2 + 1);     // pixel size of one world cell

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // ── Background ──────────────────────────────────────────────
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, size, size);

    // ── Grid lines ───────────────────────────────────────────────
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= RADIUS * 2 + 1; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL, 0);
      ctx.lineTo(i * CELL, size);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i * CELL);
      ctx.lineTo(size, i * CELL);
      ctx.stroke();
    }

    // ── Area points ──────────────────────────────────────────────
    for (const p of areaPoints) {
      const dx = p.x - center.x;
      const dy = p.y - center.y;
      if (Math.abs(dx) > RADIUS || Math.abs(dy) > RADIUS) continue;

      const px = ( dx + RADIUS) * CELL;
      const py = (-dy + RADIUS) * CELL;

      ctx.fillStyle = p.color || '#FFFFFF';
      ctx.fillRect(px + 1, py + 1, CELL - 2, CELL - 2);
    }

    // ── Center point (always draw + highlight) ───────────────────
    const cx = RADIUS * CELL;
    const cy = RADIUS * CELL;

    ctx.fillStyle = center.color || '#FFFFFF';
    ctx.fillRect(cx + 1, cy + 1, CELL - 2, CELL - 2);

    // White outline so the center is always visible
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1;
    ctx.strokeRect(cx + 0.5, cy + 0.5, CELL - 1, CELL - 1);
  }, [center, areaPoints, size, CELL, RADIUS]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block', imageRendering: 'pixelated' }}
    />
  );
};